import { rem } from 'polished'
import { useCallback, useContext, useMemo } from 'react'
import * as React from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import styled from 'styled-components'

import { buildAccommodationCancellationPolicies } from '@luxuryescapes/lib-refunds'
import LineInfoCircleIcon from 'LuxKit/Icons/line/LineInfoCircleIcon'
import Modal from 'LuxKit/Modal/Modal'
import RadioInput from 'LuxKit/Radio/RadioInput'
import TextLink from 'LuxKit/TextLink'
import BodyText from 'LuxKit/Typography/BodyText'
import CancellationPolicyTimeline from 'components/Common/CancellationPolicies/CancellationPolicyTimeline'
import cancellationPolicyTimelineMessages from 'components/Common/CancellationPolicies/cancellationPolicyTimelineMessages'
import Clickable from 'components/Common/Clickable'
import FormatCurrency from 'components/Common/FormatCurrency'
import HelpPane from 'components/Common/HelpPane'
import Group from 'components/utils/Group'
import InlineIcon from 'components/utils/InlineIcon'
import ModalContext from 'contexts/ModalContext'
import { formatHeadlineCancellationPolicyStartForDisplay } from 'lib/datetime/dateUtils'
import { showsPropertyCancellationTimeline } from 'lib/offer/cancellationPolicyUtils'

const StyledRadioInput = styled(RadioInput)`
  display: flex;
  align-items: start;
`

const CancellationClickable = styled(Clickable)`
  display: inline-flex;
  align-items: center;
  gap: ${rem(4)};
  text-decoration: none;

  &.locked {
    color: ${props => props.theme.palette.neutral.default.one};
  }
`

interface CancellationDisplayText {
  primary: string
  secondary?: string
  highlightPrimary: boolean
  /** The formatted deadline inside `secondary`, for the policy modal. */
  deadline?: string
}

interface CancellationTextOptions {
  intl: ReturnType<typeof useIntl>
  headlineCancellationPolicyType: App.EnrichedPropertyRate['headlineCancellationPolicyType']
  headlineCancellationPolicyStart: App.EnrichedPropertyRate['headlineCancellationPolicyStart']
  timezone: string
  dateFormattingLocale: string
}

function getCancellationText({
  intl,
  headlineCancellationPolicyType,
  headlineCancellationPolicyStart,
  timezone,
  dateFormattingLocale,
}: CancellationTextOptions): CancellationDisplayText {
  const nonRefundableLabel = intl.formatMessage({
    id: 'xvWHpDA1-Kplt1HmWGii',
    defaultMessage: 'Non refundable',
  })
  const freeCancellationLabel = intl.formatMessage({
    id: 'NmC8LTH4QQoMwIso1Xbh',
    defaultMessage: 'Free cancellation',
  })
  const partiallyRefundableLabel = intl.formatMessage({
    id: 'ZznnSheC7alEPdRGvXTi',
    defaultMessage: 'Partially refundable',
  })

  if (headlineCancellationPolicyType === 'nonRefundable') {
    return {
      primary: nonRefundableLabel,
      highlightPrimary: false,
    }
  }

  const refundText = headlineCancellationPolicyType === 'fullyRefundable'
    ? freeCancellationLabel
    : partiallyRefundableLabel

  const formattedDate = headlineCancellationPolicyStart &&
    formatHeadlineCancellationPolicyStartForDisplay(
      headlineCancellationPolicyStart,
      timezone,
      dateFormattingLocale,
    )

  if (formattedDate) {
    return {
      primary: refundText,
      secondary: intl.formatMessage(
        {
          id: 'hv1uXBUiZ3e8KSTtpNec',
          defaultMessage: 'until {date}',
        },
        { date: formattedDate },
      ),
      highlightPrimary: true,
      deadline: formattedDate,
    }
  }

  return {
    primary: refundText,
    highlightPrimary: true,
  }
}

interface Props {
  rate: App.EnrichedPropertyRate
  radioGroupName: string
  isSelected: boolean
  isLocked: boolean
  priceDifference: number
  staffRateLabel: string | undefined
  propertyTimezone: string
  dateFormattingLocale: string
  onChange: (event: React.ChangeEvent<HTMLInputElement>) => void
  onRemoveUpgrade?: () => void
}

function PropertyRefundabilityRateOption(props: Props) {
  const {
    rate,
    radioGroupName,
    isSelected,
    isLocked,
    priceDifference,
    staffRateLabel,
    propertyTimezone,
    dateFormattingLocale,
    onChange,
    onRemoveUpgrade,
  } = props

  const intl = useIntl()
  const showModal = useContext(ModalContext)

  const policyDescriptions = useMemo(() => buildAccommodationCancellationPolicies({
    fullRefundableUntil: rate.fullyRefundableUntil,
    cancellationPolicies: rate.cancellationPolicies.map(({
      startIsoDateTime,
      endIsoDateTime,
      penaltyPrice,
      isFullyNonRefundable,
      originalDescription,
    }) => ({
      amount: penaltyPrice,
      percent: 0,
      description: originalDescription,
      start: startIsoDateTime,
      end: endIsoDateTime,
      nonRefundable: isFullyNonRefundable,
    })),
  }, {
    timezone: propertyTimezone,
    currencyCode: rate.currency,
    hideExcludesFlights: !rate.includesFlights,
    locale: intl.locale,
  }).description, [rate, propertyTimezone, intl.locale])

  const text = getCancellationText({
    intl,
    headlineCancellationPolicyType: rate.headlineCancellationPolicyType,
    headlineCancellationPolicyStart: rate.headlineCancellationPolicyStart,
    timezone: propertyTimezone,
    dateFormattingLocale,
  })
  const timelineDeadline = showsPropertyCancellationTimeline(rate) ? text.deadline : undefined

  const timelineModalTitle = intl.formatMessage(cancellationPolicyTimelineMessages.modalTitle)

  const showCancellationDetails = useCallback((event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault()
    event.stopPropagation()

    showModal(<Modal title={timelineDeadline ? timelineModalTitle : text.primary}>
      {timelineDeadline
        ? <CancellationPolicyTimeline deadline={timelineDeadline} policyDescriptions={policyDescriptions} />
        : policyDescriptions.map((line, index) => <BodyText variant="large" as="p" key={index}>{line}</BodyText>)}
      <HelpPane />
    </Modal>)
  }, [showModal, timelineDeadline, timelineModalTitle, text.primary, policyDescriptions])

  return (
    <StyledRadioInput
      name={radioGroupName}
      value={rate.rateId}
      onChange={onChange}
      checked={isSelected}
      disabled={isLocked}
    >
      <Group direction="horizontal" gap={4} verticalAlign="center" wrap="wrap">
        <BodyText
          variant="medium"
          weight={text.highlightPrimary ? 'bold' : undefined}
          colour={text.highlightPrimary ? 'highlight-primary-dark' : (isLocked ? 'normal-contrast' : undefined)}
          wrap="no-wrap"
        >
          {text.primary}
        </BodyText>
        {text.secondary && (
          <BodyText variant="medium" colour={isLocked ? 'normal-contrast' : undefined} wrap="no-wrap">
            {text.secondary}
          </BodyText>
        )}
        {staffRateLabel && (
          <BodyText variant="medium" colour="light-foreground-alt">
            {staffRateLabel}
          </BodyText>
        )}
        {!isSelected && priceDifference > 0 && (
          <BodyText variant="medium" colour="light-foreground-alt" weight="bold" wrap="no-wrap">
            <FormattedMessage
              id="mGHDJoYlHarKV4E9f2Ln"
              defaultMessage="+{price}"
              
              values={{ price: <FormatCurrency value={priceDifference} format="roundedDollar" /> }}
            />
          </BodyText>
        )}
        {!isSelected && priceDifference < 0 && (
          <BodyText variant="medium" colour="light-foreground-alt" weight="bold" wrap="no-wrap">
            <FormatCurrency value={priceDifference} format="roundedDollar" />
          </BodyText>
        )}
        <CancellationClickable
          className={isLocked ? 'locked' : undefined}
          onClick={showCancellationDetails}
        >
          <InlineIcon Icon={LineInfoCircleIcon} />
        </CancellationClickable>
        {isLocked && <BodyText variant="medium" colour="normal-contrast">
          <FormattedMessage
            id="qkpK0JdLZD8sOuc8qyhx"
            defaultMessage="(Unavailable with free upgrade. <link>Remove upgrade</link>)"
            
            values={{
              link: (chunks) => <TextLink size="medium" colour="normal-contrast" underline onClick={onRemoveUpgrade}>{chunks}</TextLink>,
            }}
          />
        </BodyText>}
      </Group>
    </StyledRadioInput>
  )
}

export default PropertyRefundabilityRateOption
